'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DollarSign, TrendingUp, Zap, Calendar, AlertTriangle, Loader2, XCircle, RefreshCw } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { getUsageStats, getBudgetLimits, getTopChats, recalculateUsage } from '@/app/actions/usage';
import type { UsageStats, BudgetLimits, BudgetStatus } from '@/lib/usage-types';
import { BudgetSettings } from '@/components/settings/budget-settings';

function formatTokens(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return count.toString();
}

function formatCost(cost: number): string {
  if (cost >= 1) return `$${cost.toFixed(2)}`;
  if (cost >= 0.01) return `$${cost.toFixed(2)}`;
  return `$${cost.toFixed(4)}`;
}

interface TopChat {
  id: string;
  title: string;
  usage: { inputTokens: number; outputTokens: number; totalCost: number };
  created_at: string;
}

export function UsageTab() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<UsageStats | null>(null);
  const [budget, setBudget] = useState<BudgetStatus | null>(null);
  const [limits, setLimits] = useState<BudgetLimits | null>(null);
  const [topChats, setTopChats] = useState<TopChat[]>([]);
  const [recalculating, setRecalculating] = useState(false);

  // Ref to track if component is mounted (prevents memory leak)
  const isMountedRef = useRef(true);

  const loadData = useCallback(async () => {
    setError(null);
    try {
      const [statsRes, limitsRes, chatsRes] = await Promise.all([
        getUsageStats(),
        getBudgetLimits(),
        getTopChats(10),
      ]);

      // Check if component is still mounted before updating state
      if (!isMountedRef.current) return;

      if (!statsRes.success) {
        setError(statsRes.error || 'Failed to load usage stats');
        return;
      }

      if (statsRes.data) {
        setStats(statsRes.data.stats);
        setBudget(statsRes.data.budget);
      }

      if (limitsRes.success && limitsRes.data) {
        setLimits(limitsRes.data);
      }

      if (chatsRes.success && chatsRes.data) {
        setTopChats(chatsRes.data);
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to load usage data');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    loadData();

    return () => {
      isMountedRef.current = false;
    };
  }, [loadData]);

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      await recalculateUsage();
      await loadData();
    } catch (error) {
      console.error('Failed to recalculate:', error);
    } finally {
      setRecalculating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>{error}</span>
            <Button variant="outline" size="sm" onClick={() => { setLoading(true); loadData(); }}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const today = new Date().toISOString().split('T')[0];
  const currentMonth = today.slice(0, 7);

  const todayUsage = stats?.daily.filter(r => r.date === today).reduce((sum, r) => sum + r.cost, 0) || 0;
  const monthUsage = stats?.daily.filter(r => r.date.startsWith(currentMonth)).reduce((sum, r) => sum + r.cost, 0) || 0;

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold tracking-tight">Usage</h2>
        <Button variant="outline" onClick={handleRecalculate} disabled={recalculating}>
          {recalculating ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Recalculate Stats
        </Button>
      </div>

      {/* Budget Alerts */}
      {budget && budget.alerts.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{budget.alerts[0]}</AlertDescription>
        </Alert>
      )}

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <div className="p-1.5 bg-green-100 dark:bg-green-500/20 rounded">
                <DollarSign className="h-4 w-4 text-green-600 dark:text-green-400" />
              </div>
              Total Spent
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCost(stats?.grandTotal.cost || 0)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats?.grandTotal.callCount || 0} API calls
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <div className="p-1.5 bg-blue-100 dark:bg-blue-500/20 rounded">
                <Calendar className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              This Month
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCost(monthUsage)}</div>
            {limits?.monthly && (
              <p className="text-xs text-muted-foreground mt-1">
                of ${limits.monthly.toFixed(2)} limit
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <div className="p-1.5 bg-purple-100 dark:bg-purple-500/20 rounded">
                <TrendingUp className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              </div>
              Today
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCost(todayUsage)}</div>
            {limits?.daily && (
              <p className="text-xs text-muted-foreground mt-1">
                of ${limits.daily.toFixed(2)} limit
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <div className="p-1.5 bg-orange-100 dark:bg-orange-500/20 rounded">
                <Zap className="h-4 w-4 text-orange-600 dark:text-orange-400" />
              </div>
              Total Tokens
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatTokens((stats?.grandTotal.inputTokens || 0) + (stats?.grandTotal.outputTokens || 0))}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              In: {formatTokens(stats?.grandTotal.inputTokens || 0)} / Out: {formatTokens(stats?.grandTotal.outputTokens || 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Card className="overflow-hidden">
        <Tabs defaultValue="by-model" className="w-full">
          <CardHeader className="pb-0">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="shrink-0">Usage Details</CardTitle>
              <div className="overflow-x-auto -mx-2 px-2">
                <TabsList className="w-max">
                  <TabsTrigger value="by-model">By Model</TabsTrigger>
                  <TabsTrigger value="by-day">By Day</TabsTrigger>
                  <TabsTrigger value="top-chats">Top Chats</TabsTrigger>
                </TabsList>
              </div>
            </div>
          </CardHeader>

          <CardContent className="pt-6">
            <TabsContent value="by-model" className="mt-0">
              <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Model</TableHead>
                    <TableHead className="text-right">Calls</TableHead>
                    <TableHead className="text-right">Input Tokens</TableHead>
                    <TableHead className="text-right">Output Tokens</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats?.totals && Object.entries(stats.totals)
                    .sort(([, a], [, b]) => b.cost - a.cost)
                    .map(([model, data]) => (
                      <TableRow key={model}>
                        <TableCell className="font-medium">
                          <Badge variant="outline">{model}</Badge>
                        </TableCell>
                        <TableCell className="text-right">{data.callCount}</TableCell>
                        <TableCell className="text-right">{formatTokens(data.inputTokens)}</TableCell>
                        <TableCell className="text-right">{formatTokens(data.outputTokens)}</TableCell>
                        <TableCell className="text-right font-medium">{formatCost(data.cost)}</TableCell>
                      </TableRow>
                    ))}
                  {(!stats?.totals || Object.keys(stats.totals).length === 0) && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                        No usage data yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              </div>
            </TabsContent>

            <TabsContent value="by-day" className="mt-0">
              <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Model</TableHead>
                    <TableHead className="text-right">Calls</TableHead>
                    <TableHead className="text-right">Tokens</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats?.daily
                    .slice()
                    .sort((a, b) => b.date.localeCompare(a.date))
                    .slice(0, 30)
                    .map((record, idx) => (
                      <TableRow key={`${record.date}-${record.model}-${idx}`}>
                        <TableCell className="font-medium">{record.date}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{record.model}</Badge>
                        </TableCell>
                        <TableCell className="text-right">{record.callCount}</TableCell>
                        <TableCell className="text-right">
                          {formatTokens(record.inputTokens + record.outputTokens)}
                        </TableCell>
                        <TableCell className="text-right font-medium">{formatCost(record.cost)}</TableCell>
                      </TableRow>
                    ))}
                  {(!stats?.daily || stats.daily.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                        No daily usage data yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              </div>
            </TabsContent>

            <TabsContent value="top-chats" className="mt-0">
              <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Chat</TableHead>
                    <TableHead className="text-right">Tokens</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                    <TableHead className="text-right">Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topChats.map((chat) => (
                    <TableRow key={chat.id}>
                      <TableCell className="font-medium max-w-xs truncate">{chat.title}</TableCell>
                      <TableCell className="text-right">
                        {formatTokens(chat.usage.inputTokens + chat.usage.outputTokens)}
                      </TableCell>
                      <TableCell className="text-right font-medium">{formatCost(chat.usage.totalCost)}</TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {new Date(chat.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                  {topChats.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                        No chat usage data yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              </div>
            </TabsContent>
          </CardContent>
        </Tabs>
      </Card>

      {/* Budget Settings */}
      <BudgetSettings />
    </div>
  );
}
